import type { GreetingCardOrientation, GreetingCardPaperSize } from '@giftie/shared/greeting-cards'

const MM_PER_INCH = 25.4
export const GREETING_CARD_PRINT_BORDER_MM = 5
export const DEFAULT_GREETING_CARD_PAPER_SIZE: GreetingCardPaperSize = 'A5'
const GREETING_CARD_RENDER_DPI = 200

type PaperDimensions = {
  widthMm: number
  heightMm: number
}

const PAPER_SIZE_DIMENSIONS: Record<GreetingCardPaperSize, PaperDimensions> = {
  'A5': { widthMm: 148, heightMm: 210 },
  'A6': { widthMm: 105, heightMm: 148 },
  '4x6': { widthMm: 101.6, heightMm: 152.4 },
  '5x7': { widthMm: 127, heightMm: 177.8 },
}

export type GreetingCardPaperMetrics = {
  widthMm: number
  heightMm: number
  widthIn: number
  heightIn: number
  aspectRatio: string
}

export type GreetingCardPaperRenderConfig = GreetingCardPaperMetrics & {
  templatePx: { width: number, height: number }
  safeAreaPx: { x: number, y: number, width: number, height: number }
}

export function isGreetingCardPaperSize(value: unknown): value is GreetingCardPaperSize {
  return value === 'A5' || value === 'A6' || value === '4x6' || value === '5x7'
}

export function parseGreetingCardPaperSize(value: unknown) {
  return isGreetingCardPaperSize(value)
    ? value
    : DEFAULT_GREETING_CARD_PAPER_SIZE
}

export function getGreetingCardPaperMetrics(
  paperSize: GreetingCardPaperSize,
  orientation: GreetingCardOrientation,
) {
  const base = PAPER_SIZE_DIMENSIONS[paperSize]
  const widthMm = orientation === 'landscape' ? base.heightMm : base.widthMm
  const heightMm = orientation === 'landscape' ? base.widthMm : base.heightMm

  return {
    widthMm,
    heightMm,
    widthIn: widthMm / MM_PER_INCH,
    heightIn: heightMm / MM_PER_INCH,
    aspectRatio: `${widthMm} / ${heightMm}`,
  }
}

function getGreetingCardSafeMarginPx(widthMm: number, heightMm: number) {
  const shortestMm = Math.min(widthMm, heightMm)
  const shortestIn = shortestMm / MM_PER_INCH
  const marginIn = shortestIn * 0.05
  return Math.round(marginIn * GREETING_CARD_RENDER_DPI)
}

export function getGreetingCardPaperRenderConfig(
  paperSize: GreetingCardPaperSize,
  orientation: GreetingCardOrientation,
) {
  const metrics = getGreetingCardPaperMetrics(paperSize, orientation)
  const templateWidth = Math.round(metrics.widthIn * GREETING_CARD_RENDER_DPI)
  const templateHeight = Math.round(metrics.heightIn * GREETING_CARD_RENDER_DPI)
  const safeMarginPx = getGreetingCardSafeMarginPx(metrics.widthMm, metrics.heightMm)

  return {
    ...metrics,
    templatePx: {
      width: templateWidth,
      height: templateHeight,
    },
    safeAreaPx: {
      x: safeMarginPx,
      y: safeMarginPx,
      width: templateWidth - (safeMarginPx * 2),
      height: templateHeight - (safeMarginPx * 2),
    },
  }
}

export function getGreetingCardPrintBorderPercentages(
  paperSize: GreetingCardPaperSize,
  orientation: GreetingCardOrientation,
) {
  const metrics = getGreetingCardPaperMetrics(paperSize, orientation)
  return {
    horizontal: (GREETING_CARD_PRINT_BORDER_MM / metrics.widthMm) * 100,
    vertical: (GREETING_CARD_PRINT_BORDER_MM / metrics.heightMm) * 100,
  }
}

export function arePaperSizesSameAspectRatio(
  left: GreetingCardPaperSize,
  right: GreetingCardPaperSize,
) {
  const leftDimensions = PAPER_SIZE_DIMENSIONS[left]
  const rightDimensions = PAPER_SIZE_DIMENSIONS[right]
  const leftRatio = leftDimensions.widthMm / leftDimensions.heightMm
  const rightRatio = rightDimensions.widthMm / rightDimensions.heightMm
  return Math.abs(leftRatio - rightRatio) < 0.0001
}

export function classifyPaperSizeCompatibility(
  activeSize: GreetingCardPaperSize,
  supportedSizes: GreetingCardPaperSize[],
) {
  if (supportedSizes.includes(activeSize)) return 'supported'
  if (supportedSizes.some((size) => arePaperSizesSameAspectRatio(size, activeSize))) {
    return 'same_ratio'
  }
  if (supportedSizes.some((size) => size !== '4x6' && activeSize !== '4x6')) {
    return 'near_ratio'
  }
  return 'different_ratio'
}

export function calculateImageDpiForPaperSize(input: {
  widthPx: number
  heightPx: number
  paperSize: GreetingCardPaperSize
  orientation: GreetingCardOrientation
}) {
  const metrics = getGreetingCardPaperMetrics(input.paperSize, input.orientation)
  const dpiX = input.widthPx / metrics.widthIn
  const dpiY = input.heightPx / metrics.heightIn
  return Math.min(dpiX, dpiY)
}

export function getPaperSizeLabel(paperSize: GreetingCardPaperSize) {
  switch (paperSize) {
    case 'A5': {
      return 'A5'
    }
    case 'A6': {
      return 'A6'
    }
    case '4x6': {
      return '4x6 in'
    }
    case '5x7': {
      return '5x7 in'
    }
  }
}

export const GREETING_CARD_PAPER_SIZE_OPTIONS = [
  { label: 'A5 (148 x 210mm)', value: 'A5' },
  { label: 'A6 (105 x 148mm)', value: 'A6' },
  { label: '4x6 in (102 x 152mm)', value: '4x6' },
  { label: '5x7 in (127 x 178mm)', value: '5x7' },
] as const satisfies Array<{ label: string, value: GreetingCardPaperSize }>
